import { collection, getDocs, addDoc, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import type { Company } from '../../../../companies/service/companiesService';

export type CompanyFormData = {
    companyName: string;
    companyType: string;
    companyId?: string;
    companyNo?: string;
    email?: string;
    companyAddress?: string;
    contactPersonName?: string;
    contactMobileNumber?: string;
    contactEmail?: string;
    selectedCompanyFromList?: Company | null;
    selectedCCTVCompanyFromList?: Company | null;
    selectedBAMSCompanyFromList?: Company | null;
};

export async function fetchGlobalCompanies(companyType?: string): Promise<Company[]> {
    const companiesSnap = await getDocs(collection(db, 'companies'));
    let companiesList = companiesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    } as Company));

    if (companyType === 'Guard Company') {
        // Show all companies that are NOT CCTV or BAMS (or don't have companyType)
        companiesList = companiesList.filter(company => {
            const type = company.companyType;
            return !type || (type !== 'CCTV' && type !== 'BAMS');
        });
    } else if (companyType) {
        // For CCTV or BAMS, show only companies with that specific type
        companiesList = companiesList.filter(company => company.companyType === companyType);
    }

    return companiesList;
}

export async function submitCompany(
    bankId: string,
    formData: CompanyFormData,
    uid: string
): Promise<void> {
    let companyDocId: string;

    if (
        (formData.companyType === 'Guard Company' && formData.selectedCompanyFromList) ||
        (formData.companyType === 'CCTV' && formData.selectedCCTVCompanyFromList) ||
        (formData.companyType === 'BAMS' && formData.selectedBAMSCompanyFromList)
    ) {
        // Save selected company with contact person details
        const selectedCompany = formData.selectedCompanyFromList || formData.selectedCCTVCompanyFromList || formData.selectedBAMSCompanyFromList!;
        const companyRef = doc(db, 'banks', bankId, 'companies', selectedCompany.id);
        await setDoc(companyRef, {
            companyId: selectedCompany.id,
            companyType: formData.companyType,
            contactPersonName: formData.contactPersonName?.trim(),
            contactMobileNumber: formData.contactMobileNumber?.trim(),
            contactEmail: formData.contactEmail?.trim(),
            createdBy: uid,
            createdAt: new Date(),
            status: 'Active',
        });
        companyDocId = selectedCompany.id;

        // Update the company's bankClient field with BankId
        const globalCompanyRef = doc(db, 'companies', selectedCompany.id);
        const globalCompanyDoc = await getDoc(globalCompanyRef);

        if (globalCompanyDoc.exists()) {
            const currentData = globalCompanyDoc.data();
            const bankClientArray = currentData.bankClient || [];

            // Check if this bank already exists
            const existingEntry = bankClientArray.find((entry: any) => entry.BankId === bankId);

            if (!existingEntry) {
                await updateDoc(globalCompanyRef, {
                    bankClient: [...bankClientArray, {
                        BankId: bankId
                    }],
                });
            }
        }
    } else {
        // Create new company in local collection with simplified structure
        const newCompanyDoc = await addDoc(collection(db, 'banks', bankId, 'companies'), {
            companyName: formData.companyName.trim(),
            companyType: formData.companyType,
            companyId: formData.companyId?.trim(),
            companyNo: formData.companyNo?.trim(),
            email: formData.email?.trim(),
            companyAddress: formData.companyAddress?.trim(),
            contactPersonName: formData.contactPersonName?.trim(),
            contactMobileNumber: formData.contactMobileNumber?.trim(),
            contactEmail: formData.contactEmail?.trim(),
            createdBy: uid,
            createdAt: new Date(),
            status: 'Active',
        });
        companyDocId = newCompanyDoc.id;
    }

    // Add company ID to bank's guardCompanies field (only for manual entries)
    if (
        (formData.companyType === 'Guard Company' && !formData.selectedCompanyFromList) ||
        (formData.companyType === 'CCTV' && !formData.selectedCCTVCompanyFromList) ||
        (formData.companyType === 'BAMS' && !formData.selectedBAMSCompanyFromList)
    ) {
        const bankRef = doc(db, 'banks', bankId);
        const bankDoc = await getDoc(bankRef);

        if (bankDoc.exists()) {
            const currentData = bankDoc.data();
            const guardCompaniesArray = currentData.guardCompanies || [];

            // For manual companies, save the complete company data
            const companyData = {
                companyName: formData.companyName.trim(),
                companyId: formData.companyId?.trim(),
                companyNo: formData.companyNo?.trim(),
                email: formData.email?.trim(),
                companyAddress: formData.companyAddress?.trim(),
                contactPersonName: formData.contactPersonName?.trim(),
                contactMobileNumber: formData.contactMobileNumber?.trim(),
                contactEmail: formData.contactEmail?.trim(),
                companyType: formData.companyType,
                createdAt: new Date(),
                status: 'Active',
            };

            // Check if company already exists by companyId
            const existingIndex = guardCompaniesArray.findIndex(
                (item: any) => item.companyId === companyData.companyId
            );

            if (existingIndex === -1) {
                await updateDoc(bankRef, {
                    guardCompanies: [...guardCompaniesArray, companyData],
                });
            }
        } else {
            // Create bank document if it doesn't exist (shouldn't happen)
            const companyData = [{
                companyName: formData.companyName.trim(),
                companyId: formData.companyId?.trim(),
                companyNo: formData.companyNo?.trim(),
                email: formData.email?.trim(),
                companyAddress: formData.companyAddress?.trim(),
                contactPersonName: formData.contactPersonName?.trim(),
                contactMobileNumber: formData.contactMobileNumber?.trim(),
                contactEmail: formData.contactEmail?.trim(),
                companyType: formData.companyType,
                createdAt: new Date(),
                status: 'Active',
            }];

            await setDoc(bankRef, {
                guardCompanies: companyData,
                createdAt: new Date(),
            });
        }
    }
}
